import React from 'react';

import { GroupRequestCard } from '~/domains/group/components/GroupRequestCard/GroupRequestCard';
import { Routes } from '~/domains/common/constants/routes';

import ContestInviteCard from '../ContestInviteCard/ContestInviteCard';
import ContestNotificationCard from '../ContestNotificationCard/ContestNotificationCard';
import ContestRestartCard from '../ContestRestartCard/ContestRestartCard';
import OfferFeedCard from '../OfferFeedCard/OfferFeedCard';
import { resolveOfferFeedKind } from '../../utils/resolveOfferFeedKind';

import type {
  ContestNotificationWidgetInviteContent,
  ContestNotificationWidgetGroupInviteContent,
  ContestNotificationWidgetOfferContent,
  ContestNotificationWidgetNotificationContent,
  ContestNotificationWidgetProps,
  ContestNotificationWidgetRestartContent,
} from './types';
import useStyles from './ContestNotificationWidget.styles';

interface ContestNotificationWidgetContentProps extends ContestNotificationWidgetProps {
  isFullWidth?: boolean;
  displayItems:
    | ContestNotificationWidgetInviteContent['invites']
    | ContestNotificationWidgetNotificationContent['notifications']
    | ContestNotificationWidgetRestartContent['restarts']
    | ContestNotificationWidgetOfferContent['offers']
    | ContestNotificationWidgetGroupInviteContent['groupInvites'];
}

function ContestNotificationWidgetContent({
  content,
  displayItems,
  isFullWidth,
}: ContestNotificationWidgetContentProps) {
  const { classes } = useStyles();

  return (
    <div
      className={isFullWidth ? classes.widgetCardContainerFullWidth : classes.widgetCardContainer}
    >
      {content.type === 'invites' &&
        displayItems.map((item) => (
          <ContestInviteCard
            key={item.invite.id}
            contest={item.contest}
            invite={item.invite}
            onAccept={content?.onInviteAccept}
            onDecline={content?.onInviteDecline}
            onClick={content?.onInviteClick ? () => content?.onInviteClick(item.invite) : undefined}
          />
        ))}

      {content.type === 'notifications' &&
        displayItems.map((notification) => (
          <ContestNotificationCard
            key={notification.id}
            notification={notification}
            onClick={
              content.onNotificationClick
                ? () => content.onNotificationClick(notification)
                : undefined
            }
          />
        ))}

      {content.type === 'offers' &&
        displayItems.map((item) => {
          const kind = resolveOfferFeedKind(item);

          return kind === null ? null : (
            <OfferFeedCard
              key={item.offerId}
              item={item}
              kind={kind}
              href={Routes.marketplaceOffer(item.offerId)}
              isBusy={content.isBusy}
              onDecline={content.onDecline}
              onDismiss={content.onDismiss}
              onView={content.onView}
            />
          );
        })}

      {content.type === 'groupInvites' &&
        displayItems.map((invite) => <GroupRequestCard key={invite.id} invite={invite} />)}

      {content.type === 'restart' &&
        displayItems.map((restart) => (
          <ContestRestartCard
            key={restart.id}
            restart={restart}
            onRestart={content?.onRestart}
            onDismiss={content?.onDismiss}
            onContestClick={content?.onContestClick}
          />
        ))}
    </div>
  );
}

export default ContestNotificationWidgetContent;
